/** Stable guide URLs, reading order and grouping for the user handbook. */
export const DOC_GROUPS = ["start", "operations", "maintenance", "administration"] as const;

/** Documentation category. */
export type DocGroup = (typeof DOC_GROUPS)[number];

/** A guide's navigation metadata; content lives in the docs translation namespace. */
interface GuideEntry {
  id: string;
  group: DocGroup;
  sections: readonly string[];
}

/** Guides describe controls available in the SPA, not the wider backend API. */
export const DOC_GUIDES = [
  { id: "getting-started", group: "start", sections: ["access", "navigation", "roles"] },
  { id: "account-security", group: "start", sections: ["profile", "password", "mfa"] },
  { id: "daily-work", group: "start", sections: ["dashboard", "technician", "handover"] },
  { id: "assets", group: "operations", sections: ["find", "create", "components", "status"] },
  {
    id: "certificates-meters",
    group: "operations",
    sections: ["certificates", "renewal", "meters"],
  },
  { id: "fleet-map", group: "operations", sections: ["browse", "position", "missing"] },
  { id: "fault-reports", group: "operations", sections: ["report", "triage", "follow"] },
  { id: "work-orders", group: "operations", sections: ["create", "lifecycle", "review"] },
  {
    id: "execution",
    group: "operations",
    sections: ["tasks", "labour", "parts", "complete"],
  },
  {
    id: "inventory",
    group: "operations",
    sections: ["catalogue", "receive", "reorder", "warehouse", "counts"],
  },
  {
    id: "documents",
    group: "operations",
    sections: ["upload", "read", "photos", "revisions", "governance"],
  },
  { id: "notifications", group: "operations", sections: ["inbox", "preferences", "missing"] },
  { id: "reports", group: "operations", sections: ["library", "history", "schedules", "access"] },
  { id: "internal-finance", group: "operations", sections: ["costs", "chargebacks", "access"] },
  {
    id: "preventive-maintenance",
    group: "maintenance",
    sections: ["concepts", "schedules", "exceptions"],
  },
  { id: "calendar", group: "maintenance", sections: ["browse", "schedule", "generate"] },
  {
    id: "job-templates",
    group: "maintenance",
    sections: ["header", "tasks", "parts", "publish"],
  },
  { id: "plans", group: "maintenance", sections: ["create", "triggers", "targets", "check"] },
  {
    id: "members-roles",
    group: "administration",
    sections: ["invite", "members", "roles", "teams"],
  },
  { id: "sites-parties", group: "administration", sections: ["sites", "locations", "parties"] },
  {
    id: "asset-configuration",
    group: "administration",
    sections: ["types", "models", "attributes", "reference", "workspace"],
  },
  {
    id: "notification-rules",
    group: "administration",
    sections: ["create", "audience", "conditions"],
  },
  {
    id: "integrations-audit",
    group: "administration",
    sections: ["endpoints", "deliveries", "events", "audit"],
  },
  { id: "sso", group: "administration", sections: ["connections", "provisioning", "rollout"] },
  {
    id: "troubleshooting",
    group: "start",
    sections: ["access", "records", "save", "limits"],
  },
] as const satisfies readonly GuideEntry[];

/** Stable documentation article identifier. */
export type GuideId = (typeof DOC_GUIDES)[number]["id"];
